import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  DollarSign,
  Tag,
  CreditCard,
  Calendar,
  FileText,
} from "lucide-react";
import CustomSelectDropdown from "./CustomSelectDropdown";
import api from "../api/axios";

const TransactionModal = ({
  isOpen,
  onClose,
  type = "expense",
  onSuccess,
  ledgerId,
  transaction, // <-- NEW: existing transaction for edit/view
}) => {
  const isEditMode = !!transaction;

  const [formData, setFormData] = useState({
    type: type,
    amount: "",
    category: "",
    subCategory: "",
    paymentMode: "UPI",
    date: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const [errorMsg, setErrorMsg] = useState("");
  const modalRef = useRef(null);

  const categories = {
    income: ["Salary", "Business", "Investment", "Freelance", "Other Income"],
    expense: [
      "Food",
      "Transport",
      "Shopping",
      "Bills",
      "Entertainment",
      "Health",
      "Education",
      "Groceries",
      "Rent",
      "Other",
    ],
  };

  const paymentModes = [
    "Cash",
    "UPI",
    "Card",
    "Bank Transfer",
    "Net Banking",
    "Cheque",
  ];

  // Fill form when editing or when modal opens
  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode && transaction) {
      setFormData({
        type: transaction.type || type,
        amount: transaction.amount || "",
        category: transaction.category || "",
        subCategory: transaction.subCategory || "",
        paymentMode: transaction.paymentMode || "UPI",
        date: transaction.date ? transaction.date.split("T")[0] : new Date().toISOString().split("T")[0],
        notes: transaction.notes || "",
      });
    } else {
      // New transaction
      setFormData({
        type: type,
        amount: "",
        category: "",
        subCategory: "",
        paymentMode: "UPI",
        date: new Date().toISOString().split("T")[0],
        notes: "",
      });
    }

    setErrorMsg("");
  }, [isOpen, transaction, type, isEditMode]);

  // Auto scroll to top on error
  useEffect(() => {
    if (errorMsg && modalRef.current) {
      modalRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [errorMsg]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    try {
      let targetLedgerId = ledgerId;

      if (!targetLedgerId) {
        const ledgersResponse = await api.get("/ledgers");
        const ledgersData = ledgersResponse.data.data || ledgersResponse.data;
        if (!Array.isArray(ledgersData) || ledgersData.length === 0) {
          setErrorMsg("Please create a ledger first!");
          return;
        }
        targetLedgerId = ledgersData[0]._id;
      }

      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        ledgerId: targetLedgerId,
      };

      if (isEditMode) {
        await api.put(`/transactions/${transaction._id || transaction.id}`, payload);
      } else {
        await api.post("/transactions", payload);
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving transaction:", error);
      setErrorMsg(error.response?.data?.error || "Failed to save transaction");
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
          >
            <div
              ref={modalRef}
              className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-8 py-6 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {isEditMode ? "Edit" : "Add"}{" "}
                    {formData.type === "income" ? "Income" : "Expense"}
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">
                    {isEditMode
                      ? "Update transaction details"
                      : "Enter transaction details"}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} className="text-gray-500" />
                </button>
              </div>

              <AnimatePresence>
                {errorMsg && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mx-8 mt-6 mb-2 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm font-medium flex items-center gap-3"
                  >
                    <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{errorMsg}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="p-8 space-y-6">
                {/* Type Toggle - Disabled in Edit Mode */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    disabled={isEditMode}
                    onClick={() => handleChange("type", "expense")}
                    className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                      formData.type === "expense"
                        ? "bg-rose-500 text-white shadow-lg"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    } ${isEditMode ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    Expense
                  </button>
                  <button
                    type="button"
                    disabled={isEditMode}
                    onClick={() => handleChange("type", "income")}
                    className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                      formData.type === "income"
                        ? "bg-emerald-500 text-white shadow-lg"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    } ${isEditMode ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    Income
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount *
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl font-bold">
                      ₹
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.amount}
                      onChange={(e) => handleChange("amount", e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-lg font-semibold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <CustomSelectDropdown
                    value={formData.category}
                    options={categories[formData.type]}
                    onChange={(val) => handleChange("category", val)}
                    placeholder="Select category"
                    leftIcon={Tag}
                    optionIcon={Tag}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sub-category (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.subCategory}
                    onChange={(e) => handleChange("subCategory", e.target.value)}
                    placeholder="e.g., Vegetables, Uber, Amazon"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Mode *
                  </label>
                  <CustomSelectDropdown
                    value={formData.paymentMode}
                    options={paymentModes}
                    onChange={(val) => handleChange("paymentMode", val)}
                    placeholder="Select payment mode"
                    leftIcon={CreditCard}
                    optionIcon={CreditCard}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <div className="relative">
                    <Calendar
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      type="date"
                      required
                      value={formData.date}
                      onChange={(e) => handleChange("date", e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <div className="relative">
                    <FileText
                      className="absolute left-4 top-4 text-gray-400"
                      size={20}
                    />
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleChange("notes", e.target.value)}
                      placeholder="Add any additional details..."
                      rows={3}
                      className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-6">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-6 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`flex-1 px-6 py-3 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all ${
                      formData.type === "income"
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "bg-rose-600 hover:bg-rose-700"
                    }`}
                  >
                    {isEditMode ? "Update" : "Add"}{" "}
                    {formData.type === "income" ? "Income" : "Expense"}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TransactionModal;